/* eslint-disable better-mutation/no-mutation */

import { createSlice } from '@reduxjs/toolkit';
import { setEnum } from 'tradera-apps/my-tradera/app/helpers/enums';

const initialState = {
    modifiers: {
        query: {},
        paging: {}
    },
    filterBar: null,
    shownFilters: null
};

const resetPageIndex = state => ({
    page: 1,
    size: state.modifiers.paging.size
});

const slice = createSlice({
    name: 'filters',
    initialState,
    reducers: {
        updatePageSize: (state, { payload }) => {
            state.modifiers.paging.page = 1;
            state.modifiers.paging.size = payload.size;
        },
        selectSortChange: (state, { payload }) => {
            state.modifiers.sort = {
                sorting: payload.sort,
                order: payload.sortOrder
            };
            state.modifiers.paging = resetPageIndex(state);
        },
        changeSortOrder: state => {
            state.modifiers.sort = {
                sorting: state.modifiers.sort.sorting,
                order:
                    state.modifiers.sort.order === 'Descending'
                        ? 'Ascending'
                        : 'Descending'
            };
            state.modifiers.paging = resetPageIndex(state);
        },
        selectTypeChange: (state, { payload }) => {
            state.modifiers.itemType = payload.itemType;
            state.modifiers.paging = resetPageIndex(state);
        },
        setDateFrom: (state, { payload }) => {
            state.modifiers.dateRange.from = payload.from;
            state.modifiers.paging = resetPageIndex(state);
        },
        setSingleFilterValue: (state, { payload }) => {
            state.modifiers.sellerFilters[payload.filter] = payload.value;
        },
        setDateTo: (state, { payload }) => {
            state.modifiers.dateRange.to = payload.to;
            state.modifiers.paging = resetPageIndex(state);
        },
        filterByState: (state, { payload }) => {
            let showSelectFiltersDropDown = false;
            if (payload.selected === 'Valj') {
                showSelectFiltersDropDown = !state.filterBar
                    .showSelectFiltersDropDown;
            }
            setSellerFilterBarModifiers(state, payload.selected);
            state.defaults = {
                ...state.defaults
            };
            state.modifiers.selectedFilterBar = payload.selected;
            state.modifiers.paging = resetPageIndex(state);
            state.filterBar.showSelectFiltersDropDown = showSelectFiltersDropDown;
        },
        changePage: (state, { payload }) => {
            state.modifiers.paging.page = payload.page;
        },
        resetDateRangeTo: state => {
            state.modifiers.dateRange.to =
                state.defaults.modifiers.dateRange.to;
        },
        resetDateRangeFrom: state => {
            state.modifiers.dateRange.from =
                state.defaults.modifiers.dateRange.from;
        },
        resetFilters: state => {
            state.modifiers = { ...state.defaults.modifiers };
            state.filterBar = {
                ...state.filterBar,
                ...state.defaults.filterBar
            };
        },
        changeSearchQuery: (state, { payload }) => {
            state.modifiers.query.searchQuery = payload.searchQuery;
            state.modifiers.sort = {
                sorting: 'BestMatch',
                order: 'Ascending'
            };
            state.modifiers.paging = resetPageIndex(state);
        },
        selectReservationPriceChange: (state, { payload }) => {
            state.modifiers.reservePriceReached = payload.reservePriceReached;
            state.modifiers.paging = resetPageIndex(state);
        },
        updateFiltersForSelectedPage: (state, { payload }) => {
            if (!payload.newFilters.modifiers) {
                return;
            }
            Object.keys(payload.newFilters).forEach(
                key => (state[key] = payload.newFilters[key])
            );
            state.defaults = {
                ...payload.defaultFilters
            };
        },
        setFiltersFromRouter: (state, { payload }) => {
            state.modifiers = payload.filters;
        }
    }
});

export default slice.reducer;

export const {
    updatePageSize,
    selectTypeChange,
    selectSortChange,
    changeSortOrder,
    setDateFrom,
    setDateTo,
    changePage,
    changeSearchQuery,
    selectReservationPriceChange,
    setFiltersFromRouter,
    updateFiltersForSelectedPage,
    filterByState,
    setSingleFilterValue,
    resetFilters,
    resetDateRangeTo,
    resetDateRangeFrom
} = slice.actions;

/**
 * setSellerFilterBarModifiers
 * @param {object} state
 * @param {string} selected
 * @returns {object} state of 'filters' based on UI tab selection. Note: WebApi expects values as Capitalcase Enum;
 */
const setSellerFilterBarModifiers = (state, selected) => {
    if (selected === 'Valj') {
        return;
    }

    if (state.modifiers.sellerFilters) {
        Object.keys(state.modifiers.sellerFilters).forEach(key => {
            state.modifiers.sellerFilters[key] = setEnum('All');
        });
    }

    if (state.modifiers.buyerFilters) {
        Object.keys(state.modifiers.buyerFilters).forEach(key => {
            state.modifiers.buyerFilters[key] = setEnum('All');
        });
    }

    switch (selected) {
        case 'SellerNoFeedback':
            state.modifiers.sellerFilters.paid = setEnum(true);
            state.modifiers.sellerFilters.shipped = setEnum(true);
            state.modifiers.sellerFilters.feedbackLeft = setEnum(false);
            state.modifiers.sellerFilters.hidden = setEnum(false);
            break;
        case 'BuyerNoFeedback':
            state.modifiers.buyerFilters.feedbackLeft = setEnum(false);
            state.modifiers.buyerFilters.paid = setEnum(true);
            state.modifiers.buyerFilters.hidden = setEnum(false);
            break;
        case 'SellerNotPaid':
            state.modifiers.sellerFilters.paid = setEnum(false);
            state.modifiers.sellerFilters.shipped = setEnum(false);
            state.modifiers.sellerFilters.hidden = setEnum(false);
            break;
        case 'BuyerNotPaid':
            state.modifiers.buyerFilters.paid = setEnum(false);
            state.modifiers.buyerFilters.hidden = setEnum(false);
            break;
        case 'NotSent':
            state.modifiers.sellerFilters.paid = setEnum(true);
            state.modifiers.sellerFilters.shipped = setEnum(false);
            state.modifiers.sellerFilters.hidden = setEnum(false);
            break;
        case 'AllSold':
        case 'AllUnsold':
            state.modifiers.sellerFilters.hidden = setEnum(false);
            break;
        case 'SellerHidden':
            state.modifiers.sellerFilters.hidden = setEnum(true);
            break;
        case 'BuyerHidden':
            state.modifiers.buyerFilters.hidden = setEnum(true);
            break;
        case 'BuyerNotHidden':
            state.modifiers.buyerFilters.hidden = setEnum(false);
            break;
        case 'AllPurchases':
            state.modifiers.buyerFilters.hidden = setEnum(false);
            break;
        case 'AllBids':
            state.modifiers.hasBids = setEnum('All');
            break;
        case 'HasBids':
            state.modifiers.hasBids = setEnum(true);
            break;
        case 'NoBids':
            state.modifiers.hasBids = setEnum(false);
            break;
        case 'ItemsLostNotHidden':
            state.modifiers.buyerFilters.hidden = setEnum(false);
            break;
        case 'ItemsLostHidden':
            state.modifiers.buyerFilters.hidden = setEnum(true);
            break;
        default:
            break;
    }
};
